/**
 * Debounced Working Billing Claim Updates
 *
 * Provides a hook that debounces and serializes PUT calls to the
 * working billing claim endpoint. Applies optimistic cache updates
 * immediately while coalescing rapid changes into a single network request.
 *
 * Uses a module-level in-flight map to serialize PUTs across components
 * that share the same encounterId (e.g., AttestationsPopover + NoteMetadata).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getStore } from '@store/features/storeReference';
import type { WorkingClaimLineItem } from '@store/generated/encounters';
import { useAppDispatch } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { encountersApi, useUpdateWorkingBillingClaimMutation } from '../store/api';
import { buildClaimPutRequest } from '../utils/billingClaim';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DiagnosisCodeSource = 'provider_selected' | 'ai_recommended';

export interface BillingClaimUpdate {
  lineItems?: WorkingClaimLineItem[];
  diagnosisCodes?: string[];
  diagnosisCodeSources?: Record<string, DiagnosisCodeSource>;
  placeOfService?: string | null;
}

export interface UseDebouncedWorkingBillingClaimOptions {
  encounterId: string | undefined;
  /** Debounce window in ms. Default: 500 */
  debounceMs?: number;
  /** Called once after a coalesced PUT succeeds. Receives the merged update. */
  onSuccess?: (merged: BillingClaimUpdate) => void;
}

// ---------------------------------------------------------------------------
// Module-level in-flight tracking (shared across hook instances)
// ---------------------------------------------------------------------------

const inFlightByEncounter = new Map<string, Promise<void>>();

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDebouncedWorkingBillingClaim({
  encounterId,
  debounceMs = 500,
  onSuccess,
}: UseDebouncedWorkingBillingClaimOptions) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [updateWBC] = useUpdateWorkingBillingClaimMutation();

  // Mutable refs so the flush closure always reads the latest values
  const pendingRef = useRef<BillingClaimUpdate | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const encounterIdRef = useRef(encounterId);
  encounterIdRef.current = encounterId;

  // For isBusy reactive state
  const [hasPending, setHasPending] = useState(false);

  // ------------------------------------------------------------------
  // flush — send the coalesced update (waits for any in-flight PUT)
  // ------------------------------------------------------------------
  const flush = useCallback(async (): Promise<void> => {
    // Cancel any pending debounce
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }

    const eid = encounterIdRef.current;
    if (!eid || !pendingRef.current) {
      setHasPending(false);
      return;
    }

    // Wait for any in-flight PUT for this encounter (cross-component safety)
    const existing = inFlightByEncounter.get(eid);
    if (existing) {
      try {
        await existing;
      } catch {
        // Previous PUT failed — we still want to attempt ours
      }
    }

    // Snapshot and clear pending updates
    const merged = pendingRef.current;
    pendingRef.current = null;
    setHasPending(false);

    if (!merged) return;

    // Read fresh encounter from cache (has up-to-date `updated_at`)
    const selectEncounter =
      encountersApi.endpoints.getV2ProviderEncountersByEncounterId.select({
        encounterId: eid,
      });
    const state = getStore().getState();
    const claim = selectEncounter(state)?.data?.working_billing_claim ?? null;

    const putRequest = buildClaimPutRequest(claim, merged);

    // Create a promise we can track for serialization.
    // This promise never rejects — errors are handled internally.
    let resolveInFlight: () => void;
    const inFlightPromise = new Promise<void>(resolve => {
      resolveInFlight = resolve;
    });
    inFlightByEncounter.set(eid, inFlightPromise);

    try {
      await updateWBC({
        encounterId: eid,
        workingBillingClaimPutRequest: putRequest,
      }).unwrap();

      onSuccessRef.current?.(merged);
    } catch (error: unknown) {
      // Invalidate cache to get fresh data (undo any stale optimistic state)
      dispatch(
        encountersApi.util.invalidateTags([
          { type: 'Encounter' as const, id: eid },
        ]),
      );
      if (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        error.status === 409
      ) {
        enqueueSnackbar(
          'Billing data was updated elsewhere. Please refresh.',
          { variant: 'warning' },
        );
      } else {
        enqueueSnackbar('Failed to update billing data', {
          variant: 'error',
        });
      }
    } finally {
      // Release serialization lock
      resolveInFlight!();
      if (inFlightByEncounter.get(eid) === inFlightPromise) {
        inFlightByEncounter.delete(eid);
      }
    }
  }, [dispatch, updateWBC, enqueueSnackbar]);

  // ------------------------------------------------------------------
  // update — apply optimistic patch + merge + debounce
  // ------------------------------------------------------------------
  const update = useCallback(
    (partial: BillingClaimUpdate) => {
      if (!encounterId) return;

      // 1. Apply optimistic update to RTK Query cache immediately
      dispatch(
        encountersApi.util.updateQueryData(
          'getV2ProviderEncountersByEncounterId',
          { encounterId },
          (draft: any) => {
            if (!draft.working_billing_claim) {
              draft.working_billing_claim = {};
            }
            const wbc = draft.working_billing_claim;
            if (partial.lineItems !== undefined) {
              wbc.line_items = partial.lineItems;
            }
            if (partial.diagnosisCodes !== undefined) {
              wbc.diagnosis_codes = partial.diagnosisCodes;
            }
            if (partial.diagnosisCodeSources !== undefined) {
              wbc.diagnosis_code_sources = partial.diagnosisCodeSources;
            }
            if (partial.placeOfService !== undefined) {
              wbc.place_of_service = partial.placeOfService;
            }
          },
        ),
      );

      // 2. Merge with any pending updates (last write wins per field)
      pendingRef.current = { ...pendingRef.current, ...partial };
      setHasPending(true);

      // 3. Reset debounce timer
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void flush();
      }, debounceMs);
    },
    [encounterId, dispatch, flush, debounceMs],
  );

  // ------------------------------------------------------------------
  // Cleanup on unmount — flush pending changes, clear timer
  // ------------------------------------------------------------------
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      // If there are pending changes when unmounting, flush immediately
      if (pendingRef.current && encounterIdRef.current) {
        void flush();
      }
    };
  }, [flush]);

  return {
    update,
    flush,
    isBusy:
      hasPending ||
      (!!encounterId && inFlightByEncounter.has(encounterId)),
  };
}
